import {
  Baby, Users, BookOpen, GraduationCap, BarChart3,
  Smartphone, Settings, LayoutDashboard, LogOut, ClipboardCheck, Brain, Video, Gamepad2, Receipt, Wallet, Home, ClipboardList, Shield, CreditCard, Headset, Lock
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent,
  SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem,
  SidebarFooter, useSidebar,
} from "@/components/ui/sidebar";
import HelpTooltip from "@/components/HelpTooltip";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ALL_PERMISSIONS } from "@/lib/permissions";
import { useSubscription } from "@/hooks/useSubscription";
import { toast } from "sonner";

type MenuItem = { title: string; url: string; icon: any; help: string; featureKey?: string };

const parentItems: MenuItem[] = [
  { title: "لوحة ولي الأمر", url: "/dashboard/parent", icon: Home, help: "متابعة أطفالك: الدروس، الحضور، الواجبات، والتقارير" },
  { title: "الإعدادات", url: "/dashboard/settings", icon: Settings, help: "تعديل بيانات الملف الشخصي" },
];

// Build full menu from ALL_PERMISSIONS
const allMenuItems: MenuItem[] = ALL_PERMISSIONS.map(p => ({
  title: p.label,
  url: p.url,
  icon: p.icon,
  help: p.help,
  featureKey: p.key,
}));

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { signOut, roles, user, profile } = useAuth();
  const navigate = useNavigate();
  const [staffPermissions, setStaffPermissions] = useState<string[] | null>(null);
  const { canAccess, isExpired, isSuspended } = useSubscription();

  // Fetch staff permissions for non-admin roles
  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user || !profile?.nursery_id) return;
      
      // Admins see everything
      if (roles.includes("admin") || roles.includes("super_admin")) {
        setStaffPermissions(null); // null = show all
        return;
      }

      // Parents have their own menu
      if (roles.includes("parent")) return;

      // Fetch staff_id for this user
      const { data: staffData } = await supabase
        .from("staff")
        .select("id")
        .eq("user_id", user.id)
        .eq("nursery_id", profile.nursery_id)
        .maybeSingle();

      if (!staffData) {
        setStaffPermissions(["dashboard", "settings"]);
        return;
      }

      const { data: perms } = await supabase
        .from("staff_permissions" as any)
        .select("permission")
        .eq("staff_id", staffData.id);

      if (perms && perms.length > 0) {
        setStaffPermissions((perms as any[]).map((p: any) => p.permission));
      } else {
        // Fallback: if no permissions set, show all (legacy staff)
        setStaffPermissions(null);
      }
    };

    fetchPermissions();
  }, [user, profile?.nursery_id, roles]);

  const isParent = roles.includes("parent") && !roles.includes("admin") && !roles.includes("teacher") && !roles.includes("supervisor");

  let menuItems: MenuItem[];
  if (isParent) {
    menuItems = parentItems;
  } else if (staffPermissions === null) {
    // Admin or legacy: show all
    menuItems = allMenuItems;
  } else {
    // Filter by permissions
    menuItems = allMenuItems.filter(item => {
      const perm = ALL_PERMISSIONS.find(p => p.url === item.url);
      return perm && staffPermissions.includes(perm.key);
    });
  }

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const handleMenuClick = (item: MenuItem, e: React.MouseEvent) => {
    if (item.featureKey && !canAccess(item.featureKey)) {
      e.preventDefault();
      toast.error("هذه الميزة غير متاحة في خطتك الحالية", {
        description: "يرجى ترقية اشتراكك للوصول لهذه الميزة",
        action: {
          label: "ترقية الخطة",
          onClick: () => navigate("/dashboard/subscription"),
        },
      });
    }
  };

  return (
    <Sidebar collapsible="icon" side="right">
      <SidebarContent>
        <div className="p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg gradient-hero flex items-center justify-center shrink-0">
            <Baby className="w-5 h-5 text-sidebar-primary-foreground" />
          </div>
          {!collapsed && (
            <span className="text-lg font-bold text-sidebar-foreground">NurseryNet</span>
          )}
        </div>

        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/50">القائمة الرئيسية</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => {
                const locked = item.featureKey ? !canAccess(item.featureKey) : false;
                return (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton asChild>
                      <NavLink
                        to={locked ? "#" : item.url}
                        end={item.url === "/dashboard" || item.url === "/dashboard/parent"}
                        className={`hover:bg-sidebar-accent ${locked ? "opacity-50 cursor-not-allowed" : ""}`}
                        activeClassName={locked ? "" : "bg-sidebar-accent text-sidebar-primary font-medium"}
                        onClick={(e: React.MouseEvent) => handleMenuClick(item, e)}
                      >
                        <item.icon className="w-4 h-4 ml-2" />
                        {!collapsed && (
                          <>
                            <span className="flex-1">{item.title}</span>
                            {locked ? (
                              <Lock className="w-3 h-3 text-muted-foreground" />
                            ) : (
                              <HelpTooltip text={item.help} side="left" iconSize={12} />
                            )}
                          </>
                        )}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          {roles.includes("super_admin") && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild>
                <NavLink
                  to="/admin"
                  className="text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                  activeClassName="bg-amber-50 dark:bg-amber-900/20 font-medium"
                >
                  <Shield className="w-4 h-4 ml-2" />
                  {!collapsed && <span>لوحة المشرف العام</span>}
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={handleSignOut}
              className="text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent"
            >
              <LogOut className="w-4 h-4 ml-2" />
              {!collapsed && <span>تسجيل الخروج</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
